'use client';

import { useMemo } from 'react';
import { useUrlCitySlug } from '@/hooks/use-city-navigation';
import { useAiFoodOrderStore } from '@/store';

export interface CityLocation {
  /** Chỉ có giá trị khi địa chỉ thuộc đúng city đang duyệt. */
  lat?: number;
  lng?: number;
  hasLocation: boolean;
}

/**
 * Toạ độ DÙNG ĐƯỢC cho city đang duyệt trên URL.
 *
 * Từ khi trang city duyệt được bằng `city_id` mà không cần địa chỉ, chuyện user
 * có địa chỉ ở city A rồi mở link city B là bình thường. Địa chỉ khi đó KHÔNG
 * dùng cho city B được: gửi kèm `city_id=B` thì BE tính khoảng cách từ một điểm
 * cách đó hàng trăm km — ra "quán gần bạn 543.7 km", ETA vô nghĩa, và list
 * "Quanh đây" đầy quán của city cũ.
 *
 * Mọi callsite client cần lat/lng trong phạm vi `/[city]` phải đi qua hook này
 * thay vì đọc thẳng `address.lat/lng`.
 */
export function useCityLocation(): CityLocation {
  const address = useAiFoodOrderStore.use.address();
  const addressCitySlug = useAiFoodOrderStore.use.city_slug();
  // Qua `useUrlCitySlug` chứ không đọc thẳng `params.city`: URL listing rootless
  // do route `[city]` phục vụ nên `params.city` là segment listing, đọc thô sẽ
  // rơi vào nhánh "lệch city" → mất khoảng cách/ETA trên chính trang đó.
  const urlCitySlug = useUrlCitySlug();

  return useMemo(() => {
    if (!address?.lat || !address?.lng) return { hasLocation: false };

    // KHÔNG có segment city trên URL = route rootless (`/`, `/solomeal`,
    // `/danh-sach-cua-hang-*`) = "city của chính tôi". Toạ độ dùng được luôn,
    // khỏi so gì cả — chính địa chỉ đang định nghĩa trang này.
    if (!urlCitySlug) {
      return { lat: address.lat, lng: address.lng, hasLocation: true };
    }

    // Có segment city = đang xem một city cụ thể. Toạ độ chỉ dùng được nếu địa
    // chỉ thuộc đúng city đó; chưa biết city của địa chỉ (store chưa rehydrate)
    // cũng coi như chưa dùng được — thà thiếu khoảng cách một nhịp còn hơn bắn
    // query bằng toạ độ của tỉnh khác.
    if (!addressCitySlug || addressCitySlug !== urlCitySlug) {
      return { hasLocation: false };
    }

    return { lat: address.lat, lng: address.lng, hasLocation: true };
  }, [urlCitySlug, address?.lat, address?.lng, addressCitySlug]);
}
